import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  ListItemButton,
  ListItemText,
  Snackbar,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  FormGroup,
  MenuItem,
  RadioGroup,
  Radio,
  Avatar,
} from "@mui/material";
import {
  ArrowBackIosNew,
  Edit,
  Lock,
  Description,
  PrivacyTip,
  Logout,
  Campaign,
  Tune,
  BarChart,
  Verified,
  Visibility,
  ReportProblem,
  LocalOffer,
  DarkMode,
  LightMode,
  BrightnessAuto,
  Email,
  Notifications,
  Shield,
  History,
  Language as LanguageIcon,
} from "@mui/icons-material";
import { auth, db, functions } from "./firebase";
import AboutFooter from "./AboutFooter";
import LoginMap from "./LoginMap";
import { languages } from "./languages";
import { useTranslatedDictionary } from "./hooks/useTranslatedDictionary";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteField,
  addDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  arrayRemove,
  serverTimestamp,
} from "firebase/firestore";
import {
  signOut,
  reauthenticateWithCredential,
  EmailAuthProvider,
  updatePassword,
  updateEmail,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";

const UI_TEXT = {
  es: {
    settings: "Ajustes",
    editPersonalData: "Editar datos personales",
    editPersonalDataDesc: "Nombre de usuario, ciudad, país, fecha de nacimiento",
    viewOwnData: "Ver datos propios",
    viewOwnDataDesc: "Consulta tus datos registrados",
    ownDataTitle: "Mis datos",
    email: "Correo",
    notAvailable: "No disponible",
    changePassword: "Cambiar contraseña",
    changePasswordDesc: "Actualiza tu contraseña de acceso",
    promote: "Promocionar",
    promoteDesc: "Promociona tus publicaciones y perfil",
    notilineOffers: "Notiline — Ofertas y Descuentos",
    notilineOffersDesc: "Cupones y promos de nuestra otra plataforma",
    external: "EXTERNO",
    postStats: "Estadísticas de publicaciones",
    postStatsDesc: "Resumen total de interacción y promoción",
    verified: "Verificado",
    verifiedDesc: "Insignia azul para destacar tu perfil",
    verifiedActive: "Tu cuenta ya está verificada.",
    subscribeVerified: "Pagar verificado",
    verifiedPlan: "Suscripción mensual",
    verifiedPrice: "USD 4.99",
    verifiedPayHint: "Suscripción mensual de USD 4.99. Cancela cuando quieras.",
    verifiedPaymentDone: "Suscripción confirmada. Tu insignia verificada está activa.",
    verifiedPaymentError: "No se pudo procesar el pago de verificado.",
    cancelSubscription: "Cancelar suscripción",
    confirmCancelSubscription: "¿Estás seguro de que quieres cancelar tu suscripción? Perderás la insignia verificada.",
    subscriptionCancelled: "Suscripción cancelada. Tu insignia verificada ha sido removida.",
    subscriptionCancelError: "No se pudo cancelar la suscripción.",
    paypalMissing: "Falta configurar REACT_APP_PAYPAL_CLIENT_ID para mostrar el botón de pago.",
    close: "Cerrar",
    likes: "Me gusta",
    reposts: "Republicado",
    commented: "Comentado",
    shared: "Compartido",
    saved: "Guardado",
    followers: "Seguidores",
    promoted: "Promocionar",
    preferences: "Preferencias de usuario",
    preferencesDesc: "Notificaciones, perfil público y videos",
    appTheme: "Tema de la aplicación",
    appLanguage: "Idioma de la aplicación",
    searchLanguage: "Buscar idioma...",
    languageOnlyEsEn: "Los textos de la app están disponibles en español e inglés. Otros idiomas se guardan como preferencia y se mostrarán en cuanto se agreguen sus traducciones.",
    noLanguageResults: "No se encontraron idiomas.",
    privacyPolicy: "Política de Privacidad",
    privacyPolicyDesc: "Cómo usamos y protegemos tus datos",
    csaeStandards: "Estándares CSAE",
    csaeStandardsDesc: "Política contra la explotación y abuso sexual infantil",
    openExternalLink: "Abrir enlace externo",
    reportChildSafety: "Reportar seguridad infantil",
    reportChildSafetyDesc: "Denuncia contenido o conducta relacionada con CSAE",
    reportChildSafetyTitle: "Reportar problema de seguridad infantil",
    reportTypeLabel: "Tipo de reporte",
    reportTypeContent: "Contenido inapropiado",
    reportTypeGrooming: "Grooming o captación",
    reportTypeHarassment: "Acoso a menor",
    reportTypeOther: "Otro",
    reportDescriptionLabel: "Descripción del problema",
    reportDescriptionHint: "Incluye detalles útiles para investigación y moderación",
    reportUrlLabel: "URL o referencia del contenido (opcional)",
    sendReport: "Enviar denuncia",
    reportSent: "Denuncia enviada. Gracias por ayudar a proteger a los menores.",
    reportError: "No se pudo enviar la denuncia. Inténtalo nuevamente.",
    reportDescriptionRequired: "Describe el problema con al menos 20 caracteres.",
    terms: "Términos y Condiciones",
    termsDesc: "Nuestros términos de servicio",
    signOut: "Cerrar sesión",
    cancel: "Cancelar",
    save: "Guardar",
    currentPassword: "Contraseña Actual",
    newPassword: "Nueva Contraseña",
    confirmPassword: "Confirmar Contraseña",
    hidePasswords: "Ocultar contraseñas",
    showPasswords: "Mostrar contraseñas",
    receiveNotifications: "Recibir notificaciones",
    publicProfile: "Perfil público",
    autoplayVideos: "Reproducción automática de videos",
    language: "Idioma",
    theme: "Tema",
    spanish: "Español",
    english: "Inglés",
    dark: "Oscuro",
    light: "Claro",
    system: "Sistema",
    username: "Nombre de Usuario",
    city: "Ciudad",
    country: "País",
    birthDate: "Fecha de Nacimiento",
    weakPasswordHint: "Mínimo 6 caracteres",
    repeatPasswordHint: "Repite la nueva contraseña",
    editDataTitle: "Editar Datos Personales",
    changePasswordTitle: "Cambiar Contraseña",
    preferencesTitle: "Preferencias de usuario",
    dataUpdated: "Datos actualizados correctamente.",
    dataUpdateError: "No se pudo actualizar los datos.",
    passwordMismatch: "Las contraseñas nuevas no coinciden.",
    enterCurrentPassword: "Ingresa tu contraseña actual.",
    passwordUpdated: "Contraseña actualizada correctamente.",
    wrongPassword: "Contraseña actual incorrecta.",
    weakPassword: "La contraseña debe tener al menos 6 caracteres.",
    passwordUpdateError: "No se pudo cambiar la contraseña.",
    preferencesUpdated: "Preferencias actualizadas correctamente.",
    preferencesUpdateError: "No se pudo guardar tus preferencias.",
    signOutError: "No se pudo cerrar sesión.",
    understood: "Entendido",
    changeEmail: "Cambiar correo electrónico",
    changeEmailDesc: "Actualiza el correo de acceso a tu cuenta",
    changeEmailTitle: "Cambiar correo electrónico",
    newEmail: "Nuevo correo electrónico",
    currentPasswordHint: "Necesitamos tu contraseña actual para confirmar el cambio",
    emailUpdated: "Correo actualizado correctamente.",
    emailUpdateError: "No se pudo actualizar el correo.",
    emailAlreadyInUse: "Ese correo ya está en uso por otra cuenta.",
    invalidEmail: "El correo ingresado no es válido.",
    notifications: "Notificaciones",
    notificationsDesc: "Elige qué actividad quieres que te avisemos",
    notifFollowers: "Nuevos seguidores y solicitudes",
    notifFollowersDesc: "Cuando alguien te sigue, te solicita seguirte o acepta tu solicitud.",
    notifMessages: "Mensajes",
    notifMessagesDesc: "Cuando recibes un mensaje nuevo.",
    notifPosts: "Publicaciones y comentarios",
    notifPostsDesc: "Nuevas publicaciones de quienes sigues, comentarios y respuestas.",
    privacyAdvanced: "Privacidad",
    privacyAdvancedDesc: "Seguidores, búsqueda, mensajes y usuarios bloqueados",
    privacyAdvancedTitle: "Preferencias de privacidad",
    showFollowers: "Mostrar mis seguidores y seguidos",
    showFollowersDesc: "Si lo desactivás, otras personas no pueden ver tus listas ni contadores de seguidores/seguidos.",
    appearInSearch: "Aparecer en la búsqueda",
    appearInSearchDesc: "Si lo desactivás, no aparecés en los resultados de búsqueda de personas.",
    whoCanMessage: "Quién puede enviarte mensajes",
    messageAll: "Todos",
    messageFollowed: "Solo a quienes sigo",
    messageNone: "Nadie",
    blockedUsers: "Usuarios bloqueados",
    noBlockedUsers: "No bloqueaste a ningún usuario.",
    unblock: "Desbloquear",
    exportData: "Exportar mis datos",
    exportDataDesc: "Descarga una copia de tus publicaciones y perfil",
    exporting: "Exportando...",
    sessions: "Historial de sesiones",
    sessionsDesc: "Dispositivos y cierre de sesión remoto",
    sessionsTitle: "Historial de actividad",
    accountCreated: "Cuenta creada",
    lastLogin: "Último acceso",
    linkedProviders: "Proveedores vinculados",
    recentAccess: "Últimos accesos",
    noAccessLogs: "No hay registros de acceso todavía.",
    approxLocation: "Ubicación aproximada del acceso",
    closeAllSessions: "Cerrar sesión en todos los dispositivos",
    closingAllSessions: "Cerrando sesiones...",
    confirmCloseAllSessions: "Esto cerrará tu sesión en este y todos los demás dispositivos. Tendrás que volver a iniciar sesión.",
    sessionsClosed: "Sesiones cerradas. Volviendo a iniciar sesión...",
    sessionsCloseError: "No se pudo cerrar las sesiones.",
  },
  en: {
    settings: "Settings",
    editPersonalData: "Edit personal data",
    editPersonalDataDesc: "Username, city, country, birth date",
    viewOwnData: "View my data",
    viewOwnDataDesc: "Check your registered data",
    ownDataTitle: "My data",
    email: "Email",
    notAvailable: "Not available",
    changePassword: "Change password",
    changePasswordDesc: "Update your account password",
    promote: "Promote",
    promoteDesc: "Promote your posts and profile",
    notilineOffers: "Notiline — Deals & Discounts",
    notilineOffersDesc: "Coupons and deals from our other platform",
    external: "EXTERNAL",
    postStats: "Post statistics",
    postStatsDesc: "Total interaction and promotion summary",
    verified: "Verified",
    verifiedDesc: "Blue badge to highlight your profile",
    verifiedActive: "Your account is already verified.",
    subscribeVerified: "Pay for verified",
    verifiedPlan: "Monthly subscription",
    verifiedPrice: "USD 4.99",
    verifiedPayHint: "Monthly subscription for USD 4.99. Cancel anytime.",
    verifiedPaymentDone: "Subscription confirmed. Your verified badge is active.",
    verifiedPaymentError: "Could not process verified payment.",
    cancelSubscription: "Cancel subscription",
    confirmCancelSubscription: "Are you sure you want to cancel your subscription? You will lose the verified badge.",
    subscriptionCancelled: "Subscription cancelled. Your verified badge has been removed.",
    subscriptionCancelError: "Failed to cancel subscription.",
    paypalMissing: "REACT_APP_PAYPAL_CLIENT_ID is missing to render PayPal button.",
    close: "Close",
    likes: "Likes",
    reposts: "Reposted",
    commented: "Commented",
    shared: "Shared",
    saved: "Saved",
    followers: "Followers",
    promoted: "Promoted",
    preferences: "User preferences",
    preferencesDesc: "Notifications, public profile and videos",
    appTheme: "App theme",
    appLanguage: "App language",
    searchLanguage: "Search language...",
    languageOnlyEsEn: "The app's text is available in Spanish and English. Other languages are saved as a preference and will display once their translations are added.",
    noLanguageResults: "No languages found.",
    privacyPolicy: "Privacy Policy",
    privacyPolicyDesc: "How we use and protect your data",
    csaeStandards: "CSAE Standards",
    csaeStandardsDesc: "Policy against child sexual exploitation and abuse",
    openExternalLink: "Open external link",
    reportChildSafety: "Report child safety issue",
    reportChildSafetyDesc: "Report content or behavior related to CSEA",
    reportChildSafetyTitle: "Report child safety issue",
    reportTypeLabel: "Report type",
    reportTypeContent: "Inappropriate content",
    reportTypeGrooming: "Grooming or solicitation",
    reportTypeHarassment: "Harassment of a minor",
    reportTypeOther: "Other",
    reportDescriptionLabel: "Issue description",
    reportDescriptionHint: "Include details helpful for review and moderation",
    reportUrlLabel: "Content URL or reference (optional)",
    sendReport: "Send report",
    reportSent: "Report sent. Thanks for helping protect minors.",
    reportError: "Could not send the report. Please try again.",
    reportDescriptionRequired: "Please describe the issue with at least 20 characters.",
    terms: "Terms and Conditions",
    termsDesc: "Our service terms",
    signOut: "Sign out",
    cancel: "Cancel",
    save: "Save",
    currentPassword: "Current Password",
    newPassword: "New Password",
    confirmPassword: "Confirm Password",
    hidePasswords: "Hide passwords",
    showPasswords: "Show passwords",
    receiveNotifications: "Receive notifications",
    publicProfile: "Public profile",
    autoplayVideos: "Autoplay videos",
    language: "Language",
    theme: "Theme",
    spanish: "Spanish",
    english: "English",
    dark: "Dark",
    light: "Light",
    system: "System",
    username: "Username",
    city: "City",
    country: "Country",
    birthDate: "Birth date",
    weakPasswordHint: "At least 6 characters",
    repeatPasswordHint: "Repeat the new password",
    editDataTitle: "Edit Personal Data",
    changePasswordTitle: "Change Password",
    preferencesTitle: "User preferences",
    dataUpdated: "Data updated successfully.",
    dataUpdateError: "Could not update data.",
    passwordMismatch: "New passwords do not match.",
    enterCurrentPassword: "Enter your current password.",
    passwordUpdated: "Password updated successfully.",
    wrongPassword: "Current password is incorrect.",
    weakPassword: "Password must be at least 6 characters.",
    passwordUpdateError: "Could not change password.",
    preferencesUpdated: "Preferences updated successfully.",
    preferencesUpdateError: "Could not save your preferences.",
    signOutError: "Could not sign out.",
    understood: "Understood",
    changeEmail: "Change email address",
    changeEmailDesc: "Update the email used to access your account",
    changeEmailTitle: "Change email address",
    newEmail: "New email address",
    currentPasswordHint: "We need your current password to confirm this change",
    emailUpdated: "Email updated successfully.",
    emailUpdateError: "Could not update the email.",
    emailAlreadyInUse: "That email is already in use by another account.",
    invalidEmail: "The email entered is not valid.",
    notifications: "Notifications",
    notificationsDesc: "Choose what activity you want to be notified about",
    notifFollowers: "New followers and requests",
    notifFollowersDesc: "When someone follows you, requests to follow you, or accepts your request.",
    notifMessages: "Messages",
    notifMessagesDesc: "When you receive a new message.",
    notifPosts: "Posts and comments",
    notifPostsDesc: "New posts from people you follow, comments and replies.",
    privacyAdvanced: "Privacy",
    privacyAdvancedDesc: "Followers, search, messages and blocked users",
    privacyAdvancedTitle: "Privacy preferences",
    showFollowers: "Show my followers and following",
    showFollowersDesc: "If disabled, other people can't see your follower/following lists or counts.",
    appearInSearch: "Appear in search",
    appearInSearchDesc: "If disabled, you won't show up in people search results.",
    whoCanMessage: "Who can message you",
    messageAll: "Everyone",
    messageFollowed: "Only people I follow",
    messageNone: "No one",
    blockedUsers: "Blocked users",
    noBlockedUsers: "You haven't blocked any users.",
    unblock: "Unblock",
    exportData: "Export my data",
    exportDataDesc: "Download a copy of your posts and profile",
    exporting: "Exporting...",
    sessions: "Session history",
    sessionsDesc: "Devices and remote sign out",
    sessionsTitle: "Activity history",
    accountCreated: "Account created",
    lastLogin: "Last login",
    linkedProviders: "Linked providers",
    recentAccess: "Recent access",
    noAccessLogs: "No access records yet.",
    approxLocation: "Approximate access location",
    closeAllSessions: "Sign out of all devices",
    closingAllSessions: "Signing out...",
    confirmCloseAllSessions: "This will sign you out on this and every other device. You'll need to sign in again.",
    sessionsClosed: "Sessions closed. Signing you out...",
    sessionsCloseError: "Could not close the sessions.",
  },
};

const CSAE_STANDARDS_URL = "https://sky-red-social.web.app/estandares-csae.html";
const NOTILINE_URL = "https://notiline.com";

const Ajustes = ({ onBack, onNavigate, preferences, onPreferencesSaved, language = "es", themeMode = "oscuro" }) => {
  const user = auth.currentUser;
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    type: "success",
    text: "",
  });
  const [showPasswords, setShowPasswords] = useState(false);
  const [isVerifiedUser, setIsVerifiedUser] = useState(false);
  const [openVerifiedDialog, setOpenVerifiedDialog] = useState(false);
  const [payingVerified, setPayingVerified] = useState(false);
  const [openCancelSubscriptionConfirm, setOpenCancelSubscriptionConfirm] = useState(false);
  const [cancelingSubscription, setCancelingSubscription] = useState(false);
  const paypalClientId = process.env.REACT_APP_PAYPAL_CLIENT_ID || "";

  // Estados para diálogos
  const [openEditData, setOpenEditData] = useState(false);
  const [openChangePassword, setOpenChangePassword] = useState(false);
  const [openPrivacy, setOpenPrivacy] = useState(false);
  const [openTerms, setOpenTerms] = useState(false);
  const [openPreferences, setOpenPreferences] = useState(false);
  const [openThemeDialog, setOpenThemeDialog] = useState(false);
  const [savingTheme, setSavingTheme] = useState(false);
  const [openLanguageDialog, setOpenLanguageDialog] = useState(false);
  const [savingLanguage, setSavingLanguage] = useState(false);
  const [languageSearch, setLanguageSearch] = useState("");
  const [openOwnData, setOpenOwnData] = useState(false);
  const [openChildSafetyReport, setOpenChildSafetyReport] = useState(false);
  const [sendingChildSafetyReport, setSendingChildSafetyReport] = useState(false);
  const [openChangeEmail, setOpenChangeEmail] = useState(false);
  const [changingEmail, setChangingEmail] = useState(false);
  const [openNotifications, setOpenNotifications] = useState(false);
  const [savingNotifPrefs, setSavingNotifPrefs] = useState(false);
  const [openPrivacySettings, setOpenPrivacySettings] = useState(false);
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [blockedUsersList, setBlockedUsersList] = useState([]);
  const [loadingBlockedUsers, setLoadingBlockedUsers] = useState(false);
  const [exportingData, setExportingData] = useState(false);
  const [openSessions, setOpenSessions] = useState(false);
  const [accessLogs, setAccessLogs] = useState([]);
  const [openCloseAllSessionsConfirm, setOpenCloseAllSessionsConfirm] = useState(false);
  const [closingAllSessions, setClosingAllSessions] = useState(false);

  // Formulario de datos personales
  const [editDataForm, setEditDataForm] = useState({
    username: "",
    ciudad: "",
    pais: "",
    fechaNacimiento: "",
  });

  // Formulario de contraseña
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [preferencesForm, setPreferencesForm] = useState({
    receiveNotifications: true,
    publicProfile: true,
    autoplayVideos: false,
    language: "es",
    theme: "oscuro",
  });
  const [childSafetyForm, setChildSafetyForm] = useState({
    type: "content",
    description: "",
    contentUrl: "",
  });
  const [emailForm, setEmailForm] = useState({
    newEmail: "",
    currentPassword: "",
  });
  const [notificationPrefsForm, setNotificationPrefsForm] = useState({
    followers: true,
    messages: true,
    posts: true,
  });
  const [privacyForm, setPrivacyForm] = useState({
    mostrarSeguidores: true,
    apareceEnBusqueda: true,
    mensajesDe: "todos",
  });
  const activeLanguage = preferencesForm?.language || language || "es";
  const t = useTranslatedDictionary(UI_TEXT, activeLanguage);
  const selectedLanguageInfo = languages.find((l) => l.code === activeLanguage) || languages[0];
  const filteredLanguages = languages.filter((l) =>
    l.name.toLowerCase().includes(languageSearch.trim().toLowerCase()) ||
    l.code.toLowerCase().includes(languageSearch.trim().toLowerCase())
  );
  const isLightTheme = themeMode === "claro";
  const onSurface = (opacity = 1) =>
    isLightTheme ? `rgba(15,20,32,${opacity})` : `rgba(255,255,255,${opacity})`;
  const dialogBg = isLightTheme ? "#ffffff" : "#0b1016";
  const dialogGradient = isLightTheme
    ? "radial-gradient(circle at top, rgba(0,150,180,0.06) 0%, rgba(255,255,255,1) 55%)"
    : "radial-gradient(circle at top, rgba(0,212,255,0.16) 0%, rgba(11,16,22,1) 48%)";

  const openExternalUrl = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleChildSafetyFieldChange = (field) => (event) => {
    const value = event.target.value;
    setChildSafetyForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetChildSafetyForm = () => {
    setChildSafetyForm({
      type: "content",
      description: "",
      contentUrl: "",
    });
  };

  const handleSubmitChildSafetyReport = async () => {
    if (!user) return;

    const description = childSafetyForm.description.trim();
    if (description.length < 20) {
      setToast({ open: true, type: "error", text: t("reportDescriptionRequired") });
      return;
    }

    setSendingChildSafetyReport(true);
    try {
      await addDoc(collection(db, "childSafetyReports"), {
        reporterUid: user.uid,
        reporterEmail: user.email || "",
        reporterDisplayName: user.displayName || "",
        type: childSafetyForm.type,
        description,
        contentUrl: childSafetyForm.contentUrl.trim() || null,
        status: "new",
        createdAt: serverTimestamp(),
      });

      setOpenChildSafetyReport(false);
      resetChildSafetyForm();
      setToast({ open: true, type: "success", text: t("reportSent") });
    } catch (error) {
      setToast({ open: true, type: "error", text: t("reportError") });
    } finally {
      setSendingChildSafetyReport(false);
    }
  };

  useEffect(() => {
    if (!preferences) return;
    setPreferencesForm((prev) => ({ ...prev, ...preferences }));
  }, [preferences]);

  // Cargar datos del usuario
  useEffect(() => {
    const loadUserData = async () => {
      if (!user) return;
      const [userDoc, privateDoc] = await Promise.all([
        getDoc(doc(db, "users", user.uid)),
        getDoc(doc(db, "users", user.uid, "private", "data")),
      ]);
      if (userDoc.exists()) {
        const data = userDoc.data();
        const privateData = privateDoc.exists() ? privateDoc.data() : {};
        setEditDataForm({
          username: data.username || "",
          ciudad: data.ciudad || "",
          pais: data.pais || "",
          fechaNacimiento: privateData.fechaNacimiento || data.fechaNacimiento || "",
        });
        setPreferencesForm({
          receiveNotifications: data?.preferences?.receiveNotifications ?? true,
          publicProfile: data?.preferences?.publicProfile ?? true,
          autoplayVideos: data?.preferences?.autoplayVideos ?? false,
          language: data?.preferences?.language || "es",
          theme: data?.preferences?.theme || "oscuro",
        });
        setIsVerifiedUser(Boolean(data?.verified));
        setNotificationPrefsForm({
          followers: data?.notificationPrefs?.followers ?? true,
          messages: data?.notificationPrefs?.messages ?? true,
          posts: data?.notificationPrefs?.posts ?? true,
        });
        setPrivacyForm({
          mostrarSeguidores: data?.mostrarSeguidores ?? true,
          apareceEnBusqueda: data?.apareceEnBusqueda ?? true,
          mensajesDe: data?.mensajesDe || "todos",
        });
      }
    };
    loadUserData();
  }, [user]);

  const handleApproveVerified = async (data, actions) => {
    if (!user) return;
    setPayingVerified(true);
    try {
      // Para suscripciones, data contiene el subscriptionId
      const subscriptionId = data?.subscriptionId;
      if (!subscriptionId) {
        throw new Error("Subscription ID not found");
      }
      // La suscripción se valida contra PayPal en el servidor, que es el
      // único autorizado a marcar la cuenta como verificada.
      const confirmVerifiedSubscription = httpsCallable(functions, "confirmVerifiedSubscription");
      await confirmVerifiedSubscription({ subscriptionId });
      setIsVerifiedUser(true);
      setToast({ open: true, type: "success", text: t("verifiedPaymentDone") });
    } catch (error) {
      console.error("Subscription approval error:", error);
      setToast({ open: true, type: "error", text: t("verifiedPaymentError") });
    } finally {
      setPayingVerified(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!user) return;
    setCancelingSubscription(true);
    try {
      const cancelVerifiedSubscription = httpsCallable(functions, "cancelVerifiedSubscription");
      await cancelVerifiedSubscription();
      setIsVerifiedUser(false);
      setOpenCancelSubscriptionConfirm(false);
      setToast({ open: true, type: "success", text: t("subscriptionCancelled") });
    } catch (error) {
      console.error("Subscription cancellation error:", error);
      setToast({ open: true, type: "error", text: t("subscriptionCancelError") });
    } finally {
      setCancelingSubscription(false);
    }
  };

  // Guardar datos personales
  const handleSaveEditData = async () => {
    if (!user) return;
    setSaving(true);
    try {
      await setDoc(
        doc(db, "users", user.uid, "private", "data"),
        { fechaNacimiento: editDataForm.fechaNacimiento },
        { merge: true },
      );
      await updateDoc(doc(db, "users", user.uid), {
        username: editDataForm.username,
        ciudad: editDataForm.ciudad,
        pais: editDataForm.pais,
        // Limpia la copia pública heredada de versiones anteriores.
        fechaNacimiento: deleteField(),
        updatedAt: serverTimestamp(),
      });
      setOpenEditData(false);
      setToast({
        open: true,
        type: "success",
        text: t("dataUpdated"),
      });
    } catch (error) {
      setToast({
        open: true,
        type: "error",
        text: t("dataUpdateError"),
      });
    } finally {
      setSaving(false);
    }
  };

  // Cambiar contraseña
  const handleChangePassword = async () => {
    if (!user) return;
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setToast({
        open: true,
        type: "error",
        text: t("passwordMismatch"),
      });
      return;
    }
    if (!passwordForm.currentPassword) {
      setToast({
        open: true,
        type: "error",
        text: t("enterCurrentPassword"),
      });
      return;
    }
    setSaving(true);
    try {
      const credential = EmailAuthProvider.credential(
        user.email,
        passwordForm.currentPassword,
      );
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, passwordForm.newPassword);
      setOpenChangePassword(false);
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setToast({
        open: true,
        type: "success",
        text: t("passwordUpdated"),
      });
    } catch (error) {
      if (error.code === "auth/wrong-password") {
        setToast({
          open: true,
          type: "error",
          text: t("wrongPassword"),
        });
      } else if (error.code === "auth/weak-password") {
        setToast({
          open: true,
          type: "error",
          text: t("weakPassword"),
        });
      } else {
        setToast({
          open: true,
          type: "error",
          text: t("passwordUpdateError"),
        });
      }
    } finally {
      setSaving(false);
    }
  };

  // Guardar preferencias de usuario
  const handleSavePreferences = async () => {
    if (!user) return;
    setSaving(true);
    try {
      if (preferencesForm.receiveNotifications && typeof Notification !== "undefined") {
        if (Notification.permission === "default") {
          await Notification.requestPermission();
        }
      }

      await updateDoc(doc(db, "users", user.uid), {
        preferences: preferencesForm,
        updatedAt: serverTimestamp(),
      });

      if (onPreferencesSaved) {
        onPreferencesSaved(preferencesForm);
      }

      setOpenPreferences(false);
      setToast({
        open: true,
        type: "success",
        text: t("preferencesUpdated"),
      });
    } catch (error) {
      setToast({
        open: true,
        type: "error",
        text: t("preferencesUpdateError"),
      });
    } finally {
      setSaving(false);
    }
  };

  // Cambiar el tema de la aplicación
  const handleSelectTheme = async (newTheme) => {
    if (!user || newTheme === preferencesForm.theme) {
      setOpenThemeDialog(false);
      return;
    }
    setSavingTheme(true);
    const updated = { ...preferencesForm, theme: newTheme };
    try {
      await updateDoc(doc(db, "users", user.uid), {
        preferences: updated,
        updatedAt: serverTimestamp(),
      });
      setPreferencesForm(updated);
      if (onPreferencesSaved) {
        onPreferencesSaved(updated);
      }
      setToast({
        open: true,
        type: "success",
        text: t("preferencesUpdated"),
      });
    } catch (error) {
      setToast({
        open: true,
        type: "error",
        text: t("preferencesUpdateError"),
      });
    } finally {
      setSavingTheme(false);
      setOpenThemeDialog(false);
    }
  };

  // Cambiar el idioma de la aplicación
  const handleSelectLanguage = async (newLanguage) => {
    if (!user || newLanguage === preferencesForm.language) {
      setOpenLanguageDialog(false);
      return;
    }
    setSavingLanguage(true);
    const updated = { ...preferencesForm, language: newLanguage };
    try {
      await updateDoc(doc(db, "users", user.uid), {
        preferences: updated,
        updatedAt: serverTimestamp(),
      });
      setPreferencesForm(updated);
      if (onPreferencesSaved) {
        onPreferencesSaved(updated);
      }
      setToast({
        open: true,
        type: "success",
        text: t("preferencesUpdated"),
      });
    } catch (error) {
      setToast({
        open: true,
        type: "error",
        text: t("preferencesUpdateError"),
      });
    } finally {
      setSavingLanguage(false);
      setOpenLanguageDialog(false);
      setLanguageSearch("");
    }
  };

  // Cerrar sesión
  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      setToast({
        open: true,
        type: "error",
        text: t("signOutError"),
      });
    }
  };

  // Cambiar correo electrónico
  const handleChangeEmail = async () => {
    if (!user) return;
    const newEmail = emailForm.newEmail.trim();
    if (!newEmail) return;
    if (!emailForm.currentPassword) {
      setToast({ open: true, type: "error", text: t("enterCurrentPassword") });
      return;
    }
    setChangingEmail(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, emailForm.currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updateEmail(user, newEmail);
      await setDoc(
        doc(db, "users", user.uid, "private", "data"),
        { email: newEmail },
        { merge: true },
      );
      await updateDoc(doc(db, "users", user.uid), {
        email: deleteField(),
        updatedAt: serverTimestamp(),
      });
      setOpenChangeEmail(false);
      setEmailForm({ newEmail: "", currentPassword: "" });
      setToast({ open: true, type: "success", text: t("emailUpdated") });
    } catch (error) {
      let text = t("emailUpdateError");
      if (error.code === "auth/wrong-password" || error.code === "auth/invalid-credential") {
        text = t("wrongPassword");
      } else if (error.code === "auth/email-already-in-use") {
        text = t("emailAlreadyInUse");
      } else if (error.code === "auth/invalid-email") {
        text = t("invalidEmail");
      }
      setToast({ open: true, type: "error", text });
    } finally {
      setChangingEmail(false);
    }
  };

  // Guardar preferencias de notificaciones granulares
  const handleSaveNotificationPrefs = async () => {
    if (!user) return;
    setSavingNotifPrefs(true);
    try {
      await updateDoc(doc(db, "users", user.uid), {
        notificationPrefs: notificationPrefsForm,
        updatedAt: serverTimestamp(),
      });
      setOpenNotifications(false);
      setToast({ open: true, type: "success", text: t("preferencesUpdated") });
    } catch (error) {
      setToast({ open: true, type: "error", text: t("preferencesUpdateError") });
    } finally {
      setSavingNotifPrefs(false);
    }
  };

  // Privacidad avanzada: seguidores, búsqueda, mensajes y bloqueados
  const loadBlockedUsers = async (uids) => {
    if (!uids || uids.length === 0) {
      setBlockedUsersList([]);
      return;
    }
    setLoadingBlockedUsers(true);
    try {
      const profiles = await Promise.all(
        uids.map(async (blockedUid) => {
          const snap = await getDoc(doc(db, "users", blockedUid));
          return { uid: blockedUid, ...(snap.exists() ? snap.data() : {}) };
        })
      );
      setBlockedUsersList(profiles);
    } finally {
      setLoadingBlockedUsers(false);
    }
  };

  const handleOpenPrivacySettings = async () => {
    setOpenPrivacySettings(true);
    if (!user) return;
    const snap = await getDoc(doc(db, "users", user.uid));
    const uids = snap.exists() ? snap.data().blockedUids || [] : [];
    loadBlockedUsers(uids);
  };

  const handleUnblockUser = async (targetUid) => {
    if (!user) return;
    try {
      await updateDoc(doc(db, "users", user.uid), { blockedUids: arrayRemove(targetUid) });
      setBlockedUsersList((prev) => prev.filter((u) => u.uid !== targetUid));
    } catch (error) {
      setToast({ open: true, type: "error", text: t("preferencesUpdateError") });
    }
  };

  const handleSavePrivacy = async () => {
    if (!user) return;
    setSavingPrivacy(true);
    try {
      await updateDoc(doc(db, "users", user.uid), {
        mostrarSeguidores: privacyForm.mostrarSeguidores,
        apareceEnBusqueda: privacyForm.apareceEnBusqueda,
        mensajesDe: privacyForm.mensajesDe,
        updatedAt: serverTimestamp(),
      });
      setOpenPrivacySettings(false);
      setToast({ open: true, type: "success", text: t("preferencesUpdated") });
    } catch (error) {
      setToast({ open: true, type: "error", text: t("preferencesUpdateError") });
    } finally {
      setSavingPrivacy(false);
    }
  };

  const handleExportData = async () => {
    if (!user) return;
    setExportingData(true);
    try {
      const userSnap = await getDoc(doc(db, "users", user.uid));
      const postsSnap = await getDocs(query(collection(db, "posts"), where("uid", "==", user.uid)));
      const payload = {
        exportedAt: new Date().toISOString(),
        profile: userSnap.exists() ? userSnap.data() : null,
        posts: postsSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gexvi-datos-${user.uid}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setToast({ open: true, type: "error", text: t("preferencesUpdateError") });
    } finally {
      setExportingData(false);
    }
  };

  // Historial de sesiones
  const handleOpenSessions = async () => {
    setOpenSessions(true);
    if (!user) return;
    try {
      const logsSnap = await getDocs(
        query(collection(db, "users", user.uid, "accessLogs"), orderBy("timestamp", "desc"), limit(10))
      );
      setAccessLogs(logsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (error) {
      setAccessLogs([]);
    }
  };

  const handleCloseAllSessions = async () => {
    if (!user || closingAllSessions) return;
    setClosingAllSessions(true);
    try {
      const revokeOtherSessions = httpsCallable(functions, "revokeOtherSessions");
      await revokeOtherSessions();
      setOpenCloseAllSessionsConfirm(false);
      setToast({ open: true, type: "success", text: t("sessionsClosed") });
      setTimeout(() => {
        signOut(auth).catch(() => {});
      }, 1200);
    } catch (error) {
      setToast({ open: true, type: "error", text: t("sessionsCloseError") });
    } finally {
      setClosingAllSessions(false);
    }
  };

  const dialogSx = {
    "& .MuiOutlinedInput-root": {
      color: onSurface(1),
      "& fieldset": { borderColor: onSurface(0.1) },
      "&:hover fieldset": { borderColor: onSurface(0.2) },
      "&.Mui-focused fieldset": { borderColor: "#00d4ff" },
    },
    "& .MuiInputBase-input::placeholder": { color: onSurface(0.3) },
    "& .MuiInputLabel-root": { color: onSurface(0.6) },
    "& .MuiInputLabel-root.Mui-focused": { color: "#00d4ff" },
    "& .MuiFormHelperText-root": { color: onSurface(0.7) },
    "& .MuiFormHelperText-root.Mui-error": { color: "#ff6b6b" },
  };

  return (
    <Box
      sx={{
        color: onSurface(1),
        minHeight: "100dvh",
        width: "100%",
        pb: { xs: 11, sm: 10 },
        background: isLightTheme
          ? "radial-gradient(circle at top, rgba(0,150,180,0.08) 0%, #ffffff 40%, #f2f5f8 100%)"
          : "radial-gradient(circle at top, rgba(0, 212, 255, 0.14) 0%, rgba(0,0,0,0.95) 32%, #000 100%)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "sticky",
          top: 0,
          bgcolor: isLightTheme ? "rgba(255,255,255,0.82)" : "rgba(0,0,0,0.82)",
          backdropFilter: "blur(10px)",
          zIndex: 10,
        }}
      >
        <IconButton onClick={onBack} sx={{ color: onSurface(1) }}>
          <ArrowBackIosNew />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          {t("settings")}
        </Typography>
        <Box sx={{ width: 40 }} />
      </Box>

      {/* Contenido */}
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 3, maxWidth: "600px", mx: "auto" }}>
        <Stack spacing={2}>
          {/* Sección: Datos Personales */}
          <Box
            sx={{
              bgcolor: "rgba(0,212,255,0.05)",
              border: "1px solid rgba(0,212,255,0.2)",
              borderRadius: "14px",
              overflow: "hidden",
            }}
          >
            <ListItemButton
              onClick={() => setOpenEditData(true)}
              sx={{ py: 2 }}
            >
              <Edit sx={{ mr: 2, color: "#00d4ff" }} />
              <ListItemText
                primary={t("editPersonalData")}
                secondary={t("editPersonalDataDesc")}
                primaryTypographyProps={{ fontWeight: 600 }}
                secondaryTypographyProps={{ color: onSurface(0.5) }}
              />
            </ListItemButton>
          </Box>

          {/* Sección: Ver datos propios */}
          <Box
            sx={{
              bgcolor: "rgba(0,212,255,0.05)",
              border: "1px solid rgba(0,212,255,0.2)",
              borderRadius: "14px",
              overflow: "hidden",
            }}
          >
            <ListItemButton
              onClick={() => setOpenOwnData(true)}
              sx={{ py: 2 }}
            >
              <Visibility sx={{ mr: 2, color: "#00d4ff" }} />
              <ListItemText
                primary={t("viewOwnData")}
                secondary={t("viewOwnDataDesc")}
                primaryTypographyProps={{ fontWeight: 600 }}
                secondaryTypographyProps={{ color: onSurface(0.5) }}
              />
            </ListItemButton>
          </Box>

          {/* Sección: Cambiar Contraseña */}
          <Box
            sx={{
              bgcolor: "rgba(0,212,255,0.05)",
              border: "1px solid rgba(0,212,255,0.2)",
              borderRadius: "14px",
              overflow: "hidden",
            }}
          >
            <ListItemButton
              onClick={() => setOpenChangePassword(true)}
              sx={{ py: 2 }}
            >
              <Lock sx={{ mr: 2, color: "#00d4ff" }} />
              <ListItemText
                primary={t("changePassword")}
                secondary={t("changePasswordDesc")}
                primaryTypographyProps={{ fontWeight: 600 }}
                secondaryTypographyProps={{ color: onSurface(0.5) }}
              />
            </ListItemButton>
          </Box>

          {/* Sección: Cambiar correo electrónico */}
          <Box
            sx={{
              bgcolor: "rgba(0,212,255,0.05)",
              border: "1px solid rgba(0,212,255,0.2)",
              borderRadius: "14px",
              overflow: "hidden",
            }}
          >
            <ListItemButton
              onClick={() => setOpenChangeEmail(true)}
              sx={{ py: 2 }}
            >
              <Email sx={{ mr: 2, color: "#00d4ff" }} />
              <ListItemText
                primary={t("changeEmail")}
                secondary={t("changeEmailDesc")}
                primaryTypographyProps={{ fontWeight: 600 }}
                secondaryTypographyProps={{ color: onSurface(0.5) }}
              />
            </ListItemButton>
          </Box>

          {/* Sección: Promocionar */}
          <Box
            sx={{
              bgcolor: "rgba(0,212,255,0.08)",
              border: "1px solid rgba(0,212,255,0.25)",
              borderRadius: "14px",
              overflow: "hidden",
            }}
          >
            <ListItemButton
              onClick={() => onNavigate("promover")}
              sx={{ py: 2 }}
            >
              <Campaign sx={{ mr: 2, color: "#00d4ff" }} />
              <ListItemText
                primary={t("promote")}
                secondary={t("promoteDesc")}
                primaryTypographyProps={{ fontWeight: 600 }}
                secondaryTypographyProps={{ color: onSurface(0.5) }}
              />
            </ListItemButton>
          </Box>

          {/* Sección: Estadísticas de publicaciones */}
          <Box
            sx={{
              bgcolor: "rgba(0,212,255,0.05)",
              border: "1px solid rgba(0,212,255,0.2)",
              borderRadius: "14px",
              overflow: "hidden",
            }}
          >
            <ListItemButton
              onClick={() => onNavigate("estadisticas")}
              sx={{ py: 2 }}
            >
              <BarChart sx={{ mr: 2, color: "#00d4ff" }} />
              <ListItemText
                primary={t("postStats")}
                secondary={t("postStatsDesc")}
                primaryTypographyProps={{ fontWeight: 600 }}
                secondaryTypographyProps={{ color: onSurface(0.5) }}
              />
            </ListItemButton>
          </Box>

          {/* Sección: Notiline (promoción externa) */}
          <Box
            sx={{
              bgcolor: "rgba(255,152,0,0.08)",
              border: "1px solid rgba(255,152,0,0.3)",
              borderRadius: "14px",
              overflow: "hidden",
            }}
          >
            <ListItemButton
              onClick={() => openExternalUrl(NOTILINE_URL)}
              sx={{ py: 2 }}
            >
              <LocalOffer sx={{ mr: 2, color: "#ff9800" }} />
              <ListItemText
                primary={
                  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                    {t("notilineOffers")}
                    <Box
                      component="span"
                      sx={{
                        fontSize: "0.62rem",
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        bgcolor: "rgba(255,152,0,0.25)",
                        color: "#ffb74d",
                        px: 0.9,
                        py: 0.2,
                        borderRadius: "999px",
                      }}
                    >
                      {t("external")}
                    </Box>
                  </Box>
                }
                secondary={t("notilineOffersDesc")}
                primaryTypographyProps={{ fontWeight: 600 }}
                secondaryTypographyProps={{ color: onSurface(0.5) }}
              />
            </ListItemButton>
          </Box>

          {/* Sección: Verificado */}
          <Box
            sx={{
              bgcolor: isVerifiedUser ? "rgba(53,182,255,0.10)" : "rgba(0,212,255,0.05)",
              border: isVerifiedUser ? "1px solid rgba(53,182,255,0.45)" : "1px solid rgba(0,212,255,0.2)",
              borderRadius: "14px",
              overflow: "hidden",
            }}
          >
            <ListItemButton
              onClick={() => setOpenVerifiedDialog(true)}
              sx={{ py: 2 }}
            >
              <Verified sx={{ mr: 2, color: "#35b6ff" }} />
              <ListItemText
                primary={t("verified")}
                secondary={isVerifiedUser ? t("verifiedActive") : t("verifiedDesc")}
                primaryTypographyProps={{ fontWeight: 600 }}
                secondaryTypographyProps={{ color: onSurface(0.5) }}
              />
            </ListItemButton>
          </Box>

          {/* Sección: Tema de la aplicación */}
          <Box
            sx={{
              bgcolor: "rgba(0,212,255,0.05)",
              border: "1px solid rgba(0,212,255,0.2)",
              borderRadius: "14px",
              overflow: "hidden",
            }}
          >
            <ListItemButton
              onClick={() => setOpenThemeDialog(true)}
              sx={{ py: 2 }}
            >
              <DarkMode sx={{ mr: 2, color: "#00d4ff" }} />
              <ListItemText
                primary={t("appTheme")}
                secondary={t(
                  preferencesForm.theme === "claro"
                    ? "light"
                    : preferencesForm.theme === "sistema"
                    ? "system"
                    : "dark"
                )}
                primaryTypographyProps={{ fontWeight: 600 }}
                secondaryTypographyProps={{ color: onSurface(0.5) }}
              />
            </ListItemButton>
          </Box>

          {/* Sección: Idioma de la aplicación */}
          <Box
            sx={{
              bgcolor: "rgba(0,212,255,0.05)",
              border: "1px solid rgba(0,212,255,0.2)",
              borderRadius: "14px",
              overflow: "hidden",
            }}
          >
            <ListItemButton
              onClick={() => setOpenLanguageDialog(true)}
              sx={{ py: 2 }}
            >
              <LanguageIcon sx={{ mr: 2, color: "#00d4ff" }} />
              <ListItemText
                primary={t("appLanguage")}
                secondary={
                  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.6 }}>
                    <span>{selectedLanguageInfo.flag}</span>
                    <span>{selectedLanguageInfo.name}</span>
                  </Box>
                }
                primaryTypographyProps={{ fontWeight: 600 }}
                secondaryTypographyProps={{ color: onSurface(0.5), component: "div" }}
              />
            </ListItemButton>
          </Box>

          {/* Sección: Preferencias de usuario */}
          <Box
            sx={{
              bgcolor: "rgba(0,212,255,0.05)",
              border: "1px solid rgba(0,212,255,0.2)",
              borderRadius: "14px",
              overflow: "hidden",
            }}
          >
            <ListItemButton
              onClick={() => setOpenPreferences(true)}
              sx={{ py: 2 }}
            >
              <Tune sx={{ mr: 2, color: "#00d4ff" }} />
              <ListItemText
                primary={t("preferences")}
                secondary={t("preferencesDesc")}
                primaryTypographyProps={{ fontWeight: 600 }}
                secondaryTypographyProps={{ color: onSurface(0.5) }}
              />
            </ListItemButton>
          </Box>

          {/* Sección: Notificaciones granulares */}
          <Box
            sx={{
              bgcolor: "rgba(0,212,255,0.05)",
              border: "1px solid rgba(0,212,255,0.2)",
              borderRadius: "14px",
              overflow: "hidden",
            }}
          >
            <ListItemButton
              onClick={() => setOpenNotifications(true)}
              sx={{ py: 2 }}
            >
              <Notifications sx={{ mr: 2, color: "#00d4ff" }} />
              <ListItemText
                primary={t("notifications")}
                secondary={t("notificationsDesc")}
                primaryTypographyProps={{ fontWeight: 600 }}
                secondaryTypographyProps={{ color: onSurface(0.5) }}
              />
            </ListItemButton>
          </Box>

          {/* Sección: Privacidad avanzada */}
          <Box
            sx={{
              bgcolor: "rgba(0,212,255,0.05)",
              border: "1px solid rgba(0,212,255,0.2)",
              borderRadius: "14px",
              overflow: "hidden",
            }}
          >
            <ListItemButton
              onClick={handleOpenPrivacySettings}
              sx={{ py: 2 }}
            >
              <Shield sx={{ mr: 2, color: "#00d4ff" }} />
              <ListItemText
                primary={t("privacyAdvanced")}
                secondary={t("privacyAdvancedDesc")}
                primaryTypographyProps={{ fontWeight: 600 }}
                secondaryTypographyProps={{ color: onSurface(0.5) }}
              />
            </ListItemButton>
          </Box>

          {/* Sección: Historial de sesiones */}
          <Box
            sx={{
              bgcolor: "rgba(0,212,255,0.05)",
              border: "1px solid rgba(0,212,255,0.2)",
              borderRadius: "14px",
              overflow: "hidden",
            }}
          >
            <ListItemButton
              onClick={handleOpenSessions}
              sx={{ py: 2 }}
            >
              <History sx={{ mr: 2, color: "#00d4ff" }} />
              <ListItemText
                primary={t("sessions")}
                secondary={t("sessionsDesc")}
                primaryTypographyProps={{ fontWeight: 600 }}
                secondaryTypographyProps={{ color: onSurface(0.5) }}
              />
            </ListItemButton>
          </Box>

          <Divider sx={{ borderColor: onSurface(0.1), my: 1 }} />

          {/* Sección: Política y Privacidad */}
          <Box
            sx={{
              bgcolor: "rgba(100,150,200,0.05)",
              border: "1px solid rgba(100,150,200,0.2)",
              borderRadius: "14px",
              overflow: "hidden",
            }}
          >
            <ListItemButton onClick={() => setOpenPrivacy(true)} sx={{ py: 2 }}>
              <PrivacyTip sx={{ mr: 2, color: "#64b5f6" }} />
              <ListItemText
                primary={t("privacyPolicy")}
                secondary={t("privacyPolicyDesc")}
                primaryTypographyProps={{ fontWeight: 600 }}
                secondaryTypographyProps={{ color: onSurface(0.5) }}
              />
            </ListItemButton>
          </Box>

          <Box
            sx={{
              bgcolor: "rgba(100,150,200,0.05)",
              border: "1px solid rgba(100,150,200,0.2)",
              borderRadius: "14px",
              overflow: "hidden",
            }}
          >
            <ListItemButton onClick={() => openExternalUrl(CSAE_STANDARDS_URL)} sx={{ py: 2 }}>
              <Visibility sx={{ mr: 2, color: "#64b5f6" }} />
              <ListItemText
                primary={t("csaeStandards")}
                secondary={t("csaeStandardsDesc")}
                primaryTypographyProps={{ fontWeight: 600 }}
                secondaryTypographyProps={{ color: onSurface(0.5) }}
              />
            </ListItemButton>
          </Box>

          <Box
            sx={{
              bgcolor: "rgba(255,120,120,0.08)",
              border: "1px solid rgba(255,120,120,0.35)",
              borderRadius: "14px",
              overflow: "hidden",
            }}
          >
            <ListItemButton onClick={() => setOpenChildSafetyReport(true)} sx={{ py: 2 }}>
              <ReportProblem sx={{ mr: 2, color: "#ff9090" }} />
              <ListItemText
                primary={t("reportChildSafety")}
                secondary={t("reportChildSafetyDesc")}
                primaryTypographyProps={{ fontWeight: 600 }}
                secondaryTypographyProps={{ color: onSurface(0.6) }}
              />
            </ListItemButton>
          </Box>

          {/* Sección: Términos y Condiciones */}
          <Box
            sx={{
              bgcolor: "rgba(100,150,200,0.05)",
              border: "1px solid rgba(100,150,200,0.2)",
              borderRadius: "14px",
              overflow: "hidden",
            }}
          >
            <ListItemButton onClick={() => setOpenTerms(true)} sx={{ py: 2 }}>
              <Description sx={{ mr: 2, color: "#64b5f6" }} />
              <ListItemText
                primary={t("terms")}
                secondary={t("termsDesc")}
                primaryTypographyProps={{ fontWeight: 600 }}
                secondaryTypographyProps={{ color: onSurface(0.5) }}
              />
            </ListItemButton>
          </Box>

          <Divider sx={{ borderColor: onSurface(0.1), my: 1 }} />

          {/* Cerrar Sesión */}
          <Button
            onClick={handleSignOut}
            fullWidth
            sx={{
              py: 1.5,
              bgcolor: "rgba(255,77,77,0.15)",
              color: "#ff6b6b",
              border: "1px solid rgba(255,77,77,0.3)",
              borderRadius: "12px",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": { bgcolor: "rgba(255,77,77,0.25)" },
            }}
            startIcon={<Logout />}
          >
            {t("signOut")}
          </Button>
        </Stack>
      </Box>

      <AboutFooter isLightTheme={isLightTheme} language={activeLanguage} />

      {/* Diálogo: Editar Datos */}
      <Dialog
        open={openEditData}
        onClose={() => !saving && setOpenEditData(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: dialogBg,
            color: onSurface(1),
            borderRadius: "16px",
            backgroundImage: dialogGradient,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 0.5, pt: 2.5 }}>
          {t("editDataTitle")}
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2.5 }}
        >
          <TextField
            label={t("username")}
            value={editDataForm.username}
            onChange={(e) =>
              setEditDataForm((prev) => ({
                ...prev,
                username: e.target.value.toLowerCase().replace(/\s+/g, "_"),
              }))
            }
            fullWidth
            disabled={saving}
            sx={dialogSx}
          />
          <TextField
            label={t("city")}
            value={editDataForm.ciudad}
            onChange={(e) =>
              setEditDataForm((prev) => ({ ...prev, ciudad: e.target.value }))
            }
            fullWidth
            disabled={saving}
            sx={dialogSx}
          />
          <TextField
            label={t("country")}
            value={editDataForm.pais}
            onChange={(e) =>
              setEditDataForm((prev) => ({ ...prev, pais: e.target.value }))
            }
            fullWidth
            disabled={saving}
            sx={dialogSx}
          />
          <TextField
            label={t("birthDate")}
            type="date"
            value={editDataForm.fechaNacimiento}
            onChange={(e) =>
              setEditDataForm((prev) => ({
                ...prev,
                fechaNacimiento: e.target.value,
              }))
            }
            fullWidth
            disabled={saving}
            InputLabelProps={{ shrink: true }}
            sx={{
              ...dialogSx,
              "& input::-webkit-calendar-picker-indicator": {
                filter: "invert(1) brightness(1.2)",
                cursor: "pointer",
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1.5 }}>
          <Button
            onClick={() => setOpenEditData(false)}
            disabled={saving}
            sx={{ color: onSurface(0.75), textTransform: "none" }}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleSaveEditData}
            disabled={saving}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              minWidth: 100,
              background: "linear-gradient(90deg, #00d4ff 0%, #1498ff 100%)",
            }}
          >
            {saving ? (
              <CircularProgress size={20} sx={{ color: "#00131a" }} />
            ) : (
              t("save")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo: Ver datos propios */}
      <Dialog
        open={openOwnData}
        onClose={() => setOpenOwnData(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: dialogBg,
            color: onSurface(1),
            borderRadius: "16px",
            backgroundImage: dialogGradient,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 0.8, pt: 2.5 }}>
          {t("ownDataTitle")}
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Stack spacing={1.2}>
            <Box sx={{ p: 1.2, borderRadius: "10px", bgcolor: onSurface(0.03), border: `1px solid ${onSurface(0.1)}` }}>
              <Typography variant="caption" sx={{ color: onSurface(0.6) }}>{t("email")}</Typography>
              <Typography sx={{ fontWeight: 600 }}>{user?.email || t("notAvailable")}</Typography>
            </Box>
            <Box sx={{ p: 1.2, borderRadius: "10px", bgcolor: onSurface(0.03), border: `1px solid ${onSurface(0.1)}` }}>
              <Typography variant="caption" sx={{ color: onSurface(0.6) }}>{t("username")}</Typography>
              <Typography sx={{ fontWeight: 600 }}>{editDataForm.username || t("notAvailable")}</Typography>
            </Box>
            <Box sx={{ p: 1.2, borderRadius: "10px", bgcolor: onSurface(0.03), border: `1px solid ${onSurface(0.1)}` }}>
              <Typography variant="caption" sx={{ color: onSurface(0.6) }}>{t("city")}</Typography>
              <Typography sx={{ fontWeight: 600 }}>{editDataForm.ciudad || t("notAvailable")}</Typography>
            </Box>
            <Box sx={{ p: 1.2, borderRadius: "10px", bgcolor: onSurface(0.03), border: `1px solid ${onSurface(0.1)}` }}>
              <Typography variant="caption" sx={{ color: onSurface(0.6) }}>{t("country")}</Typography>
              <Typography sx={{ fontWeight: 600 }}>{editDataForm.pais || t("notAvailable")}</Typography>
            </Box>
            <Box sx={{ p: 1.2, borderRadius: "10px", bgcolor: onSurface(0.03), border: `1px solid ${onSurface(0.1)}` }}>
              <Typography variant="caption" sx={{ color: onSurface(0.6) }}>{t("birthDate")}</Typography>
              <Typography sx={{ fontWeight: 600 }}>{editDataForm.fechaNacimiento || t("notAvailable")}</Typography>
            </Box>
            <Box sx={{ p: 1.2, borderRadius: "10px", bgcolor: "rgba(53,182,255,0.08)", border: "1px solid rgba(53,182,255,0.28)" }}>
              <Typography variant="caption" sx={{ color: onSurface(0.6) }}>{t("verified")}</Typography>
              <Typography sx={{ fontWeight: 700, color: isVerifiedUser ? "#35b6ff" : onSurface(1) }}>
                {isVerifiedUser ? t("verifiedActive") : t("verifiedDesc")}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5 }}>
          <Button
            onClick={() => setOpenOwnData(false)}
            sx={{ color: onSurface(0.75), textTransform: "none" }}
          >
            {t("close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo: Suscripción Verificado */}
      <Dialog
        open={openVerifiedDialog}
        onClose={() => !payingVerified && setOpenVerifiedDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: dialogBg,
            color: onSurface(1),
            borderRadius: "16px",
            backgroundImage:
              "radial-gradient(circle at top, rgba(53,182,255,0.18) 0%, rgba(11,16,22,1) 52%)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 0.5, pt: 2.5 }}>
          {t("subscribeVerified")}
        </DialogTitle>
        <DialogContent sx={{ pt: 2.2 }}>
          <Box
            sx={{
              p: 1.8,
              borderRadius: "12px",
              border: "1px solid rgba(53,182,255,0.35)",
              bgcolor: "rgba(53,182,255,0.08)",
              mb: 1.6,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.8 }}>
              <Verified sx={{ color: "#35b6ff" }} />
              <Typography sx={{ fontWeight: 700 }}>{t("verifiedPlan")}</Typography>
            </Stack>
            <Typography sx={{ fontWeight: 800, fontSize: "1.08rem" }}>
              {t("verifiedPrice")}
            </Typography>
            <Typography variant="body2" sx={{ color: onSurface(0.74), mt: 0.45 }}>
              {t("verifiedPayHint")}
            </Typography>
          </Box>

          {isVerifiedUser ? (
            <Alert severity="success" sx={{ bgcolor: "rgba(76,175,80,0.14)", color: onSurface(1) }}>
              {t("verifiedActive")}
            </Alert>
          ) : !paypalClientId ? (
            <Alert severity="warning" sx={{ bgcolor: "rgba(255,193,7,0.14)", color: onSurface(1) }}>
              {t("paypalMissing")}
            </Alert>
          ) : (
            <Box sx={{ bgcolor: isLightTheme ? "#eef2f5" : "#111820", borderRadius: "12px", p: 1.3, border: `1px solid ${onSurface(0.08)}` }}>
              <PayPalScriptProvider
                options={{
                  clientId: paypalClientId,
                  currency: "USD",
                  intent: "subscription",
                  vault: true,
                }}
              >
                <PayPalButtons
                  style={{ layout: "vertical", shape: "pill", color: "black", label: "paypal", tagline: false }}
                  createSubscription={(data, actions) =>
                    actions.subscription.create({
                      plan_id: "P-8EH29038MW8704234NLJHHPA",
                      custom_id: user?.uid,
                      subscriber: {
                        name: {
                          given_name: user?.displayName || user?.email?.split("@")[0] || "Usuario",
                        },
                        email_address: user?.email,
                      },
                    })
                  }
                  onApprove={handleApproveVerified}
                  onError={() => {
                    setToast({ open: true, type: "error", text: t("verifiedPaymentError") });
                  }}
                  disabled={payingVerified || isVerifiedUser}
                />
              </PayPalScriptProvider>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1 }}>
          {isVerifiedUser && (
            <Button
              onClick={() => setOpenCancelSubscriptionConfirm(true)}
              disabled={cancelingSubscription}
              sx={{ 
                color: "#ff4444", 
                textTransform: "none",
                "&:hover": { bgcolor: "rgba(255,68,68,0.08)" }
              }}
            >
              {cancelingSubscription ? (
                <CircularProgress size={16} sx={{ mr: 1 }} />
              ) : null}
              {t("cancelSubscription")}
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={() => setOpenVerifiedDialog(false)}
            disabled={payingVerified || cancelingSubscription}
            sx={{ color: onSurface(0.75), textTransform: "none" }}
          >
            {t("close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo: Confirmar cancelación de suscripción */}
      <Dialog
        open={openCancelSubscriptionConfirm}
        onClose={() => !cancelingSubscription && setOpenCancelSubscriptionConfirm(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: dialogBg,
            color: onSurface(1),
            borderRadius: "16px",
            backgroundImage:
              "radial-gradient(circle at top, rgba(255,0,0,0.12) 0%, rgba(11,16,22,1) 48%)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 0.5, pt: 2.5 }}>
          {t("cancelSubscription")}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2.5 }}>
          <Typography>{t("confirmCancelSubscription")}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1.5 }}>
          <Button
            onClick={() => setOpenCancelSubscriptionConfirm(false)}
            disabled={cancelingSubscription}
            sx={{ color: onSurface(0.75), textTransform: "none" }}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleCancelSubscription}
            disabled={cancelingSubscription}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              minWidth: 100,
              background: "linear-gradient(90deg, #ff4444 0%, #cc0000 100%)",
            }}
          >
            {cancelingSubscription ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              t("cancelSubscription")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo: Cambiar Contraseña */}
      <Dialog
        open={openChangePassword}
        onClose={() => !saving && setOpenChangePassword(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: dialogBg,
            color: onSurface(1),
            borderRadius: "16px",
            backgroundImage: dialogGradient,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 0.5, pt: 2.5 }}>
          {t("changePasswordTitle")}
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2.5 }}
        >
          <TextField
            label={t("currentPassword")}
            type={showPasswords ? "text" : "password"}
            value={passwordForm.currentPassword}
            onChange={(e) =>
              setPasswordForm((prev) => ({
                ...prev,
                currentPassword: e.target.value,
              }))
            }
            fullWidth
            disabled={saving}
            sx={dialogSx}
          />
          <TextField
            label={t("newPassword")}
            type={showPasswords ? "text" : "password"}
            value={passwordForm.newPassword}
            onChange={(e) =>
              setPasswordForm((prev) => ({
                ...prev,
                newPassword: e.target.value,
              }))
            }
            fullWidth
            disabled={saving}
            placeholder={t("weakPasswordHint")}
            sx={dialogSx}
          />
          <TextField
            label={t("confirmPassword")}
            type={showPasswords ? "text" : "password"}
            value={passwordForm.confirmPassword}
            onChange={(e) =>
              setPasswordForm((prev) => ({
                ...prev,
                confirmPassword: e.target.value,
              }))
            }
            fullWidth
            disabled={saving}
            placeholder={t("repeatPasswordHint")}
            sx={dialogSx}
          />
          <Button
            size="small"
            onClick={() => setShowPasswords(!showPasswords)}
            sx={{
              color: "#00d4ff",
              textTransform: "none",
              justifyContent: "flex-start",
              pl: 0,
            }}
          >
            {showPasswords ? t("hidePasswords") : t("showPasswords")}
          </Button>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1.5 }}>
          <Button
            onClick={() => setOpenChangePassword(false)}
            disabled={saving}
            sx={{ color: onSurface(0.75), textTransform: "none" }}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleChangePassword}
            disabled={saving}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              minWidth: 100,
              background: "linear-gradient(90deg, #00d4ff 0%, #1498ff 100%)",
            }}
          >
            {saving ? (
              <CircularProgress size={20} sx={{ color: "#00131a" }} />
            ) : (
              t("save")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo: Tema de la aplicación */}
      <Dialog
        open={openThemeDialog}
        onClose={() => !savingTheme && setOpenThemeDialog(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            bgcolor: dialogBg,
            color: onSurface(1),
            borderRadius: "16px",
            backgroundImage: dialogGradient,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 0.5, pt: 2.5 }}>
          {t("appTheme")}
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <RadioGroup
            value={preferencesForm.theme}
            onChange={(e) => handleSelectTheme(e.target.value)}
          >
            {[
              { value: "sistema", label: t("system"), icon: <BrightnessAuto sx={{ color: "#00d4ff" }} /> },
              { value: "claro", label: t("light"), icon: <LightMode sx={{ color: "#00d4ff" }} /> },
              { value: "oscuro", label: t("dark"), icon: <DarkMode sx={{ color: "#00d4ff" }} /> },
            ].map((opt) => (
              <FormControlLabel
                key={opt.value}
                value={opt.value}
                disabled={savingTheme}
                control={<Radio sx={{ color: onSurface(0.4), "&.Mui-checked": { color: "#00d4ff" } }} />}
                label={
                  <Stack direction="row" spacing={1.2} alignItems="center">
                    {opt.icon}
                    <Typography sx={{ fontWeight: 600 }}>{opt.label}</Typography>
                  </Stack>
                }
                sx={{
                  mx: 0,
                  mb: 1,
                  py: 0.5,
                  px: 1.2,
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: preferencesForm.theme === opt.value ? "#00d4ff" : onSurface(0.1),
                  bgcolor: preferencesForm.theme === opt.value ? "rgba(0,212,255,0.1)" : "transparent",
                }}
              />
            ))}
          </RadioGroup>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5 }}>
          <Button
            onClick={() => setOpenThemeDialog(false)}
            disabled={savingTheme}
            sx={{ color: onSurface(0.75), textTransform: "none" }}
          >
            {savingTheme ? <CircularProgress size={18} sx={{ color: "#00d4ff" }} /> : t("cancel")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo: Idioma de la aplicación */}
      <Dialog
        open={openLanguageDialog}
        onClose={() => {
          if (savingLanguage) return;
          setOpenLanguageDialog(false);
          setLanguageSearch("");
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            bgcolor: dialogBg,
            color: onSurface(1),
            borderRadius: "16px",
            backgroundImage: dialogGradient,
            maxHeight: "80vh",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 0.5, pt: 2.5 }}>
          {t("appLanguage")}
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5, display: "flex", flexDirection: "column", gap: 1.2 }}>
          <Typography variant="body2" sx={{ color: onSurface(0.55) }}>
            {t("languageOnlyEsEn")}
          </Typography>
          <TextField
            placeholder={t("searchLanguage")}
            value={languageSearch}
            onChange={(e) => setLanguageSearch(e.target.value)}
            fullWidth
            size="small"
            disabled={savingLanguage}
            sx={dialogSx}
          />
          <Box sx={{ overflowY: "auto", maxHeight: "48vh", pr: 0.5 }}>
            {filteredLanguages.map((item) => {
              const isSelected = item.code === preferencesForm.language;
              return (
                <Box
                  key={item.code}
                  onClick={() => !savingLanguage && handleSelectLanguage(item.code)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                    px: 1.2,
                    py: 1,
                    mb: 0.6,
                    borderRadius: "10px",
                    cursor: "pointer",
                    border: "1px solid",
                    borderColor: isSelected ? "#00d4ff" : onSurface(0.08),
                    bgcolor: isSelected ? "rgba(0,212,255,0.1)" : "transparent",
                    "&:hover": { bgcolor: isSelected ? "rgba(0,212,255,0.14)" : onSurface(0.04) },
                  }}
                >
                  <Typography sx={{ fontSize: "1.2rem", lineHeight: 1 }}>{item.flag}</Typography>
                  <Typography sx={{ flex: 1, fontWeight: isSelected ? 700 : 500 }}>{item.name}</Typography>
                  {isSelected && (savingLanguage
                    ? <CircularProgress size={16} sx={{ color: "#00d4ff" }} />
                    : <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#00d4ff" }} />
                  )}
                </Box>
              );
            })}
            {filteredLanguages.length === 0 && (
              <Typography sx={{ color: onSurface(0.5), textAlign: "center", py: 3 }}>
                {t("noLanguageResults")}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5 }}>
          <Button
            onClick={() => {
              setOpenLanguageDialog(false);
              setLanguageSearch("");
            }}
            disabled={savingLanguage}
            sx={{ color: onSurface(0.75), textTransform: "none" }}
          >
            {t("close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo: Preferencias de usuario */}
      <Dialog
        open={openPreferences}
        onClose={() => !saving && setOpenPreferences(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: dialogBg,
            color: onSurface(1),
            borderRadius: "16px",
            backgroundImage: dialogGradient,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 0.5, pt: 2.5 }}>
          {t("preferencesTitle")}
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2.5 }}
        >
          <FormGroup>
            <FormControlLabel
              control={(
                <Switch
                  checked={preferencesForm.receiveNotifications}
                  onChange={(e) =>
                    setPreferencesForm((prev) => ({
                      ...prev,
                      receiveNotifications: e.target.checked,
                    }))
                  }
                  disabled={saving}
                />
              )}
              label={t("receiveNotifications")}
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={preferencesForm.publicProfile}
                  onChange={(e) =>
                    setPreferencesForm((prev) => ({
                      ...prev,
                      publicProfile: e.target.checked,
                    }))
                  }
                  disabled={saving}
                />
              )}
              label={t("publicProfile")}
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={preferencesForm.autoplayVideos}
                  onChange={(e) =>
                    setPreferencesForm((prev) => ({
                      ...prev,
                      autoplayVideos: e.target.checked,
                    }))
                  }
                  disabled={saving}
                />
              )}
              label={t("autoplayVideos")}
            />
          </FormGroup>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1.5 }}>
          <Button
            onClick={() => setOpenPreferences(false)}
            disabled={saving}
            sx={{ color: onSurface(0.75), textTransform: "none" }}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleSavePreferences}
            disabled={saving}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              minWidth: 100,
              background: "linear-gradient(90deg, #00d4ff 0%, #1498ff 100%)",
            }}
          >
            {saving ? (
              <CircularProgress size={20} sx={{ color: "#00131a" }} />
            ) : (
              t("save")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo: Cambiar correo electrónico */}
      <Dialog
        open={openChangeEmail}
        onClose={() => !changingEmail && setOpenChangeEmail(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: dialogBg,
            color: onSurface(1),
            borderRadius: "16px",
            backgroundImage: dialogGradient,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 0.5, pt: 2.5 }}>
          {t("changeEmailTitle")}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2.5 }}>
          <Typography variant="body2" sx={{ color: onSurface(0.6) }}>
            {t("email")}: {user?.email || t("notAvailable")}
          </Typography>
          <TextField
            label={t("newEmail")}
            type="email"
            value={emailForm.newEmail}
            onChange={(e) => setEmailForm((prev) => ({ ...prev, newEmail: e.target.value }))}
            fullWidth
            disabled={changingEmail}
            sx={dialogSx}
          />
          <TextField
            label={t("currentPassword")}
            type={showPasswords ? "text" : "password"}
            value={emailForm.currentPassword}
            onChange={(e) => setEmailForm((prev) => ({ ...prev, currentPassword: e.target.value }))}
            fullWidth
            disabled={changingEmail}
            helperText={t("currentPasswordHint")}
            sx={dialogSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1.5 }}>
          <Button
            onClick={() => setOpenChangeEmail(false)}
            disabled={changingEmail}
            sx={{ color: onSurface(0.75), textTransform: "none" }}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleChangeEmail}
            disabled={changingEmail || !emailForm.newEmail}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              minWidth: 100,
              background: "linear-gradient(90deg, #00d4ff 0%, #1498ff 100%)",
            }}
          >
            {changingEmail ? (
              <CircularProgress size={20} sx={{ color: "#00131a" }} />
            ) : (
              t("save")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo: Notificaciones granulares */}
      <Dialog
        open={openNotifications}
        onClose={() => !savingNotifPrefs && setOpenNotifications(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: dialogBg,
            color: onSurface(1),
            borderRadius: "16px",
            backgroundImage: dialogGradient,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 0.5, pt: 2.5 }}>
          {t("notifications")}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 0.5, pt: 2.5 }}>
          {[
            { field: "followers", label: t("notifFollowers"), desc: t("notifFollowersDesc") },
            { field: "messages", label: t("notifMessages"), desc: t("notifMessagesDesc") },
            { field: "posts", label: t("notifPosts"), desc: t("notifPostsDesc") },
          ].map((item) => (
            <Box key={item.field} sx={{ mb: 1.5 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationPrefsForm[item.field]}
                    onChange={(e) =>
                      setNotificationPrefsForm((prev) => ({ ...prev, [item.field]: e.target.checked }))
                    }
                    disabled={savingNotifPrefs}
                  />
                }
                label={item.label}
              />
              <Typography variant="body2" sx={{ color: onSurface(0.5), ml: 6, mt: -0.5 }}>
                {item.desc}
              </Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1.5 }}>
          <Button
            onClick={() => setOpenNotifications(false)}
            disabled={savingNotifPrefs}
            sx={{ color: onSurface(0.75), textTransform: "none" }}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleSaveNotificationPrefs}
            disabled={savingNotifPrefs}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              minWidth: 100,
              background: "linear-gradient(90deg, #00d4ff 0%, #1498ff 100%)",
            }}
          >
            {savingNotifPrefs ? (
              <CircularProgress size={20} sx={{ color: "#00131a" }} />
            ) : (
              t("save")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo: Privacidad avanzada */}
      <Dialog
        open={openPrivacySettings}
        onClose={() => !savingPrivacy && setOpenPrivacySettings(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: dialogBg,
            color: onSurface(1),
            borderRadius: "16px",
            backgroundImage: dialogGradient,
            maxHeight: "84vh",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 0.5, pt: 2.5 }}>
          {t("privacyAdvancedTitle")}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1, pt: 2.5 }}>
          <Box sx={{ mb: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={privacyForm.mostrarSeguidores}
                  onChange={(e) => setPrivacyForm((prev) => ({ ...prev, mostrarSeguidores: e.target.checked }))}
                  disabled={savingPrivacy}
                />
              }
              label={t("showFollowers")}
            />
            <Typography variant="body2" sx={{ color: onSurface(0.5), ml: 6, mt: -0.5 }}>
              {t("showFollowersDesc")}
            </Typography>
          </Box>

          <Box sx={{ mb: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={privacyForm.apareceEnBusqueda}
                  onChange={(e) => setPrivacyForm((prev) => ({ ...prev, apareceEnBusqueda: e.target.checked }))}
                  disabled={savingPrivacy}
                />
              }
              label={t("appearInSearch")}
            />
            <Typography variant="body2" sx={{ color: onSurface(0.5), ml: 6, mt: -0.5 }}>
              {t("appearInSearchDesc")}
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: onSurface(0.7), mb: 0.5 }}>
            {t("whoCanMessage")}
          </Typography>
          <TextField
            select
            value={privacyForm.mensajesDe}
            onChange={(e) => setPrivacyForm((prev) => ({ ...prev, mensajesDe: e.target.value }))}
            fullWidth
            size="small"
            disabled={savingPrivacy}
            sx={{ ...dialogSx, mb: 1.5 }}
          >
            <MenuItem value="todos">{t("messageAll")}</MenuItem>
            <MenuItem value="seguidos">{t("messageFollowed")}</MenuItem>
            <MenuItem value="nadie">{t("messageNone")}</MenuItem>
          </TextField>

          <Divider sx={{ borderColor: onSurface(0.1), my: 1 }} />

          <Typography sx={{ fontWeight: 700, mb: 1 }}>{t("blockedUsers")}</Typography>
          {loadingBlockedUsers ? (
            <CircularProgress size={22} sx={{ color: "#00d4ff", alignSelf: "center", my: 1 }} />
          ) : blockedUsersList.length === 0 ? (
            <Typography variant="body2" sx={{ color: onSurface(0.55), mb: 1.5 }}>
              {t("noBlockedUsers")}
            </Typography>
          ) : (
            <Stack spacing={1} sx={{ mb: 1.5 }}>
              {blockedUsersList.map((blockedUser) => (
                <Box
                  key={blockedUser.uid}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                    p: 1,
                    borderRadius: "10px",
                    bgcolor: onSurface(0.03),
                    border: `1px solid ${onSurface(0.08)}`,
                  }}
                >
                  <Avatar src={blockedUser.photoURL} sx={{ width: 34, height: 34 }} />
                  <Typography sx={{ flex: 1, fontWeight: 600 }}>
                    {blockedUser.displayName || blockedUser.username || t("user")}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => handleUnblockUser(blockedUser.uid)}
                    sx={{ textTransform: "none", color: "#00d4ff" }}
                  >
                    {t("unblock")}
                  </Button>
                </Box>
              ))}
            </Stack>
          )}

          <Divider sx={{ borderColor: onSurface(0.1), my: 1 }} />

          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>{t("exportData")}</Typography>
          <Typography variant="body2" sx={{ color: onSurface(0.55), mb: 1 }}>
            {t("exportDataDesc")}
          </Typography>
          <Button
            onClick={handleExportData}
            disabled={exportingData}
            variant="outlined"
            sx={{
              alignSelf: "flex-start",
              textTransform: "none",
              borderRadius: "10px",
              color: "#00d4ff",
              borderColor: "rgba(0,212,255,0.4)",
            }}
          >
            {exportingData ? (
              <CircularProgress size={18} sx={{ mr: 1, color: "#00d4ff" }} />
            ) : null}
            {exportingData ? t("exporting") : t("exportData")}
          </Button>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1.5 }}>
          <Button
            onClick={() => setOpenPrivacySettings(false)}
            disabled={savingPrivacy}
            sx={{ color: onSurface(0.75), textTransform: "none" }}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleSavePrivacy}
            disabled={savingPrivacy}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              minWidth: 100,
              background: "linear-gradient(90deg, #00d4ff 0%, #1498ff 100%)",
            }}
          >
            {savingPrivacy ? (
              <CircularProgress size={20} sx={{ color: "#00131a" }} />
            ) : (
              t("save")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo: Historial de sesiones */}
      <Dialog
        open={openSessions}
        onClose={() => setOpenSessions(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: dialogBg,
            color: onSurface(1),
            borderRadius: "16px",
            backgroundImage: dialogGradient,
            maxHeight: "84vh",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 0.5, pt: 2.5 }}>
          {t("sessionsTitle")}
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Stack spacing={1.2} sx={{ mb: 2 }}>
            <Box sx={{ p: 1.2, borderRadius: "10px", bgcolor: onSurface(0.03), border: `1px solid ${onSurface(0.1)}` }}>
              <Typography variant="caption" sx={{ color: onSurface(0.6) }}>{t("email")}</Typography>
              <Typography sx={{ fontWeight: 600 }}>{user?.email || t("notAvailable")}</Typography>
            </Box>
            <Box sx={{ p: 1.2, borderRadius: "10px", bgcolor: onSurface(0.03), border: `1px solid ${onSurface(0.1)}` }}>
              <Typography variant="caption" sx={{ color: onSurface(0.6) }}>{t("accountCreated")}</Typography>
              <Typography sx={{ fontWeight: 600 }}>{user?.metadata?.creationTime || t("notAvailable")}</Typography>
            </Box>
            <Box sx={{ p: 1.2, borderRadius: "10px", bgcolor: onSurface(0.03), border: `1px solid ${onSurface(0.1)}` }}>
              <Typography variant="caption" sx={{ color: onSurface(0.6) }}>{t("lastLogin")}</Typography>
              <Typography sx={{ fontWeight: 600 }}>{user?.metadata?.lastSignInTime || t("notAvailable")}</Typography>
            </Box>
            <Box sx={{ p: 1.2, borderRadius: "10px", bgcolor: onSurface(0.03), border: `1px solid ${onSurface(0.1)}` }}>
              <Typography variant="caption" sx={{ color: onSurface(0.6) }}>{t("linkedProviders")}</Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {(user?.providerData || []).map((p) => p.providerId).join(", ") || t("notAvailable")}
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ borderColor: onSurface(0.1), my: 1.5 }} />

          <Typography sx={{ fontWeight: 700, mb: 1 }}>{t("recentAccess")}</Typography>
          {accessLogs.length === 0 ? (
            <Typography variant="body2" sx={{ color: onSurface(0.55), mb: 2 }}>
              {t("noAccessLogs")}
            </Typography>
          ) : (
            <Stack spacing={1} sx={{ mb: 2 }}>
              {accessLogs.map((log) => (
                <Box key={log.id} sx={{ p: 1, borderRadius: "10px", bgcolor: onSurface(0.03), border: `1px solid ${onSurface(0.07)}` }}>
                  <Typography variant="body2">
                    {log.timestamp?.toDate ? log.timestamp.toDate().toLocaleString() : t("notAvailable")}
                  </Typography>
                  {log.location && (
                    <Typography variant="body2" sx={{ color: onSurface(0.8), fontWeight: 600 }}>
                      {[log.location.city, log.location.region, log.location.country].filter(Boolean).join(", ")}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color: onSurface(0.5) }}>
                    {log.userAgent || t("notAvailable")}
                  </Typography>
                  {log.location && (
                    <LoginMap lat={log.location.lat} lng={log.location.lng} label={t("approxLocation")} />
                  )}
                </Box>
              ))}
            </Stack>
          )}

          <Divider sx={{ borderColor: onSurface(0.1), my: 1.5 }} />

          <Button
            onClick={() => setOpenCloseAllSessionsConfirm(true)}
            variant="outlined"
            fullWidth
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              color: "#ff6b6b",
              borderColor: "rgba(255,77,77,0.4)",
            }}
          >
            {t("closeAllSessions")}
          </Button>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5 }}>
          <Button
            onClick={() => setOpenSessions(false)}
            sx={{ color: onSurface(0.75), textTransform: "none" }}
          >
            {t("close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo: Confirmar cierre de todas las sesiones */}
      <Dialog
        open={openCloseAllSessionsConfirm}
        onClose={() => !closingAllSessions && setOpenCloseAllSessionsConfirm(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: dialogBg,
            color: onSurface(1),
            borderRadius: "16px",
            backgroundImage:
              "radial-gradient(circle at top, rgba(255,0,0,0.12) 0%, rgba(11,16,22,1) 48%)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 0.5, pt: 2.5 }}>
          {t("closeAllSessions")}
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography>{t("confirmCloseAllSessions")}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1.5 }}>
          <Button
            onClick={() => setOpenCloseAllSessionsConfirm(false)}
            disabled={closingAllSessions}
            sx={{ color: onSurface(0.75), textTransform: "none" }}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleCloseAllSessions}
            disabled={closingAllSessions}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              minWidth: 100,
              background: "linear-gradient(90deg, #ff4444 0%, #cc0000 100%)",
            }}
          >
            {closingAllSessions ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              t("closeAllSessions")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo: Política de Privacidad */}
      <Dialog
        open={openPrivacy}
        onClose={() => setOpenPrivacy(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: dialogBg,
            color: onSurface(1),
            borderRadius: "16px",
            backgroundImage: dialogGradient,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Política de Privacidad
        </DialogTitle>
        <DialogContent
          sx={{ color: onSurface(0.85), fontSize: "0.9rem" }}
        >
          <Typography variant="body2" sx={{ mt: 2, lineHeight: 1.8 }}>
            <strong>1. Información que Recopilamos</strong>
            <br />
            Recopilamos información que proporcionas directamente, incluyendo
            nombre de usuario, email, fecha de nacimiento, ubicación y foto de
            perfil.
          </Typography>

          <Typography variant="body2" sx={{ mt: 2, lineHeight: 1.8 }}>
            <strong>2. Cómo Usamos tu Información</strong>
            <br />
            Usamos tu información para proporcionar, mantener y mejorar nuestros
            servicios, así como para seguridad y cumplimiento legal.
          </Typography>

          <Typography variant="body2" sx={{ mt: 2, lineHeight: 1.8 }}>
            <strong>3. Protección de Datos</strong>
            <br />
            Implementamos medidas de seguridad técnicas y organizativas para
            proteger tu información personal contra acceso no autorizado.
          </Typography>

          <Typography variant="body2" sx={{ mt: 2, lineHeight: 1.8 }}>
            <strong>4. Compartir Información</strong>
            <br />
            No vendemos tu información personal. Solo la compartimos cuando es
            necesario con proveedores de servicios de confianza.
          </Typography>

          <Typography variant="body2" sx={{ mt: 2, lineHeight: 1.8 }}>
            <strong>5. Tus Derechos</strong>
            <br />
            Tienes derecho a acceder, corregir o eliminar tu información
            personal en cualquier momento.
          </Typography>

          <Typography variant="body2" sx={{ mt: 2, lineHeight: 1.8 }}>
            <strong>6. Estándares CSAE</strong>
            <br />
            Consulta nuestros estándares contra la explotación y el abuso sexual infantil en:
            <br />
            <a href={CSAE_STANDARDS_URL} target="_blank" rel="noopener noreferrer" style={{ color: "#64b5f6" }}>
              {CSAE_STANDARDS_URL}
            </a>
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5 }}>
          <Button
            onClick={() => openExternalUrl(CSAE_STANDARDS_URL)}
            sx={{ textTransform: "none", color: "#8fd8ff" }}
          >
            {t("openExternalLink")}
          </Button>
          <Button
            onClick={() => setOpenPrivacy(false)}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              background: "linear-gradient(90deg, #00d4ff 0%, #1498ff 100%)",
            }}
          >
            {t("understood")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo: Términos y Condiciones */}
      <Dialog
        open={openTerms}
        onClose={() => setOpenTerms(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: dialogBg,
            color: onSurface(1),
            borderRadius: "16px",
            backgroundImage: dialogGradient,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Términos y Condiciones
        </DialogTitle>
        <DialogContent
          sx={{ color: onSurface(0.85), fontSize: "0.9rem" }}
        >
          <Typography variant="body2" sx={{ mt: 2, lineHeight: 1.8 }}>
            <strong>1. Aceptación de Términos</strong>
            <br />
            Al usar Gexvi, aceptas estos términos y condiciones en su
            totalidad.
          </Typography>

          <Typography variant="body2" sx={{ mt: 2, lineHeight: 1.8 }}>
            <strong>2. Conducta del Usuario</strong>
            <br />
            Aceptas no usar Gexvi para actividades ilegales, ofensivas u
            objacionables.
          </Typography>

          <Typography variant="body2" sx={{ mt: 2, lineHeight: 1.8 }}>
            <strong>3. Contenido del Usuario</strong>
            <br />
            Eres responsable del contenido que publicas. Nos reservamos el
            derecho de eliminar contenido que viole nuestras políticas.
          </Typography>

          <Typography variant="body2" sx={{ mt: 2, lineHeight: 1.8 }}>
            <strong>4. Propiedad Intelectual</strong>
            <br />
            Todo el contenido en Gexvi está protegido por derechos de autor.
            No puedes reproducir contenido sin permiso.
          </Typography>

          <Typography variant="body2" sx={{ mt: 2, lineHeight: 1.8 }}>
            <strong>5. Limitación de Responsabilidad</strong>
            <br />
            Gexvi se proporciona "tal cual". No somos responsables por daños
            indirectos o incidentales.
          </Typography>

          <Typography variant="body2" sx={{ mt: 2, lineHeight: 1.8 }}>
            <strong>6. Cambios de Términos</strong>
            <br />
            Nos reservamos el derecho de modificar estos términos en cualquier
            momento.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5 }}>
          <Button
            onClick={() => setOpenTerms(false)}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              background: "linear-gradient(90deg, #00d4ff 0%, #1498ff 100%)",
            }}
          >
            {t("understood")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openChildSafetyReport}
        onClose={() => !sendingChildSafetyReport && setOpenChildSafetyReport(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: dialogBg,
            color: onSurface(1),
            borderRadius: "16px",
            backgroundImage:
              "radial-gradient(circle at top, rgba(255,90,90,0.16) 0%, rgba(11,16,22,1) 48%)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {t("reportChildSafetyTitle")}
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Stack spacing={1.5}>
            <TextField
              select
              label={t("reportTypeLabel")}
              value={childSafetyForm.type}
              onChange={handleChildSafetyFieldChange("type")}
              fullWidth
              size="small"
              InputLabelProps={{ sx: { color: onSurface(0.75) } }}
              SelectProps={{ sx: { color: onSurface(1) } }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: onSurface(1),
                  "& fieldset": { borderColor: onSurface(0.2) },
                },
              }}
            >
              <MenuItem value="content">{t("reportTypeContent")}</MenuItem>
              <MenuItem value="grooming">{t("reportTypeGrooming")}</MenuItem>
              <MenuItem value="harassment">{t("reportTypeHarassment")}</MenuItem>
              <MenuItem value="other">{t("reportTypeOther")}</MenuItem>
            </TextField>

            <TextField
              label={t("reportDescriptionLabel")}
              placeholder={t("reportDescriptionHint")}
              value={childSafetyForm.description}
              onChange={handleChildSafetyFieldChange("description")}
              multiline
              minRows={4}
              fullWidth
              InputLabelProps={{ sx: { color: onSurface(0.75) } }}
              sx={{
                "& .MuiInputBase-input": { color: onSurface(1) },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: onSurface(0.2) },
                },
              }}
            />

            <TextField
              label={t("reportUrlLabel")}
              value={childSafetyForm.contentUrl}
              onChange={handleChildSafetyFieldChange("contentUrl")}
              fullWidth
              size="small"
              InputLabelProps={{ sx: { color: onSurface(0.75) } }}
              sx={{
                "& .MuiInputBase-input": { color: onSurface(1) },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: onSurface(0.2) },
                },
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1.5 }}>
          <Button
            onClick={() => setOpenChildSafetyReport(false)}
            disabled={sendingChildSafetyReport}
            sx={{ color: onSurface(0.75), textTransform: "none" }}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleSubmitChildSafetyReport}
            disabled={sendingChildSafetyReport}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              minWidth: 120,
              background: "linear-gradient(90deg, #ff7d7d 0%, #ff4d4d 100%)",
            }}
          >
            {sendingChildSafetyReport ? (
              <CircularProgress size={20} sx={{ color: "#310000" }} />
            ) : (
              t("sendReport")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.type} sx={{ borderRadius: "12px" }}>
          {toast.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Ajustes;
